import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  showErrorToast,
} from '@bpinternal/ui-kit-next'
import { Spinner } from '~/elements'
import { createFileRoute } from '@tanstack/react-router'
import { AlertCircle, Check, Copy, Terminal } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { NewPersonalAccessToken } from '~/features/users/types'
import { getApiClient } from '~/stores'
import { z } from 'zod'

const authorizedHosts = ['botpress.cloud', 'localhost', 'botpress.one', 'botpress.sh', 'vertical-one-demo.botpress.sh']

function isAuthorizedUrl(urlString: string): boolean {
  try {
    const { hostname } = new URL(urlString)
    return authorizedHosts.some((h) => h.toLowerCase() === hostname.toLowerCase())
  } catch {
    return false // invalid URL
  }
}

const paramsSchema = z.object({
  callback: z.string().refine(isAuthorizedUrl, { message: 'Invalid callback URL' }).optional().catch(undefined),
  redirect: z.string().refine(isAuthorizedUrl, { message: 'Invalid redirect URL' }).optional().catch(undefined),
})
export const Route = createFileRoute('/cli-login')({
  component: RouteComponent,
  validateSearch: paramsSchema,
})

const client = getApiClient()

const MAX_RETRIES = 5
const RETRY_DELAY = 1000
const FALLBACK_DELAY = 5000

function RouteComponent() {
  const [status, setStatus] = useState<'attempting' | 'success' | 'failed'>('attempting')
  const [copied, setCopied] = useState(false)
  const [showFallback, setShowFallback] = useState(false)
  const [pat, setPat] = useState<NewPersonalAccessToken | null>(null)
  const [creatingPat, setCreatingPat] = useState(true)
  const [authorized, setAuthorized] = useState(false)
  const { callback: callbackUrl, redirect: redirectUrl } = Route.useSearch()
  const isCreatingPat = useRef(false)

  useEffect(() => {
    if (!authorized) return

    const createPatAndAuth = async () => {
      if (!callbackUrl && !redirectUrl) {
        setShowFallback(true)
        setStatus('failed')
        return
      }
      if (isCreatingPat.current) return
      isCreatingPat.current = true
      // First, create the PAT
      try {
        setCreatingPat(true)
        const newPatResponse = await client.createPersonalAccessToken({
          note: `CLI Login ${new Date().toISOString()}`,
        })

        if (!newPatResponse) {
          showErrorToast('Failed to create authentication token')
          setStatus('failed')
          setCreatingPat(false)
          return
        }

        setPat(newPatResponse.pat)
        setCreatingPat(false)

        // If redirect URL is provided, redirect to it with the PAT
        if (redirectUrl) {
          const url = new URL(redirectUrl)
          url.searchParams.set('pat', newPatResponse.pat.value)
          window.location.href = url.toString()
          setStatus('success')
          return
        }

        // Otherwise, attempt to authenticate with the CLI via callback
        let attempts = 0

        while (attempts < MAX_RETRIES) {
          if (!callbackUrl) {
            setStatus('failed')
            return
          }
          try {
            const response = await fetch(callbackUrl, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({ token: newPatResponse.pat.value }),
            })

            if (response.ok) {
              setStatus('success')
              return
            }
          } catch (error) {
            console.log('Failed to connect to localhost:', error)
          }

          attempts++

          if (attempts < MAX_RETRIES) {
            await new Promise((resolve) => setTimeout(resolve, RETRY_DELAY))
          }
        }

        setStatus('failed')
      } catch (error) {
        console.error('Error creating PAT or authenticating:', error)
        showErrorToast('Failed to create authentication token')
        setStatus('failed')
        setCreatingPat(false)
      }
    }

    createPatAndAuth()

    // Show fallback after 5 seconds regardless
    const fallbackTimer = setTimeout(() => {
      setShowFallback(true)
    }, FALLBACK_DELAY)

    return () => clearTimeout(fallbackTimer)
  }, [authorized, callbackUrl, redirectUrl])

  const copyToClipboard = async () => {
    if (!pat?.value) return

    try {
      await navigator.clipboard.writeText(pat.value)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch (err) {
      console.error('Failed to copy token to clipboard:', err)
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-4">
      <Card className="w-full max-w-lg">
        <CardHeader className="space-y-2">
          <div className="flex items-center gap-2">
            <Terminal className="h-6 w-6 text-primary" />
            <CardTitle className="text-2xl">CLI Authentication</CardTitle>
          </div>
          <CardDescription>
            {!authorized ? 'Authorization Required' : 'Authenticating your CLI session'}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {!authorized ? (
            <div className="space-y-6">
              <div className="space-y-4 rounded-lg border border-border bg-muted/50 p-6">
                <p className="text-center leading-relaxed text-foreground">
                  <strong className="font-semibold">{callbackUrl || redirectUrl || 'An application'}</strong> is
                  requesting full access to your Botpress account.
                </p>
                <p className="text-center text-sm text-muted-foreground">
                  If you did not willingly originate this request, please close this page.
                </p>
              </div>
              <Button onClick={() => setAuthorized(true)} className="w-full" size="lg">
                Authorize
              </Button>
            </div>
          ) : (
            <>
              <div className="space-y-4">
                {status === 'attempting' && (
                  <div className="flex items-center gap-3 rounded-lg bg-muted p-4">
                    <Spinner className="h-5 w-5 text-primary" />
                    <div className="flex-1">
                      <p className="font-medium text-foreground">
                        {creatingPat ? 'Creating authentication token...' : 'Connecting to CLI...'}
                      </p>
                    </div>
                  </div>
                )}

                {status === 'success' && (
                  <div className="flex items-center gap-3 rounded-lg border border-accent/20 bg-accent/10 p-4">
                    <div className="flex h-8 w-8 items-center justify-center rounded-full bg-accent">
                      <Check className="h-5 w-5 text-accent-foreground" />
                    </div>
                    <div className="flex-1">
                      <p className="font-medium text-foreground">Authentication successful!</p>
                      <p className="text-sm text-muted-foreground">
                        Your CLI is now authenticated, you may now close this window.
                      </p>
                    </div>
                  </div>
                )}

                {status === 'failed' && (
                  <div className="flex items-center gap-3 rounded-lg border border-destructive/20 bg-destructive/10 p-4">
                    <AlertCircle className="h-5 w-5 text-destructive" />
                    <div className="flex-1">
                      <p className="font-medium text-foreground">Connection failed</p>
                      <p className="text-sm text-muted-foreground">Please use the manual method below</p>
                    </div>
                  </div>
                )}
              </div>

              {showFallback && status !== 'success' && (
                <div className="space-y-4 border-t border-border pt-4">
                  <div className="space-y-2">
                    <h3 className="font-semibold text-foreground">Manual Authentication</h3>
                    <p className="text-sm leading-relaxed text-muted-foreground">
                      Copy the token below and paste it into your CLI when prompted:
                    </p>
                  </div>

                  <div className="space-y-3">
                    <div className="relative">
                      <code className="block break-all rounded-lg bg-muted p-4 pr-12 font-mono text-sm text-foreground">
                        {pat?.value || 'Loading...'}
                      </code>
                      <Button
                        size="icon"
                        variant="ghost"
                        className="absolute right-2 top-2"
                        onClick={copyToClipboard}
                        disabled={!pat?.value}
                      >
                        {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                      </Button>
                    </div>
                  </div>

                  <div className="flex w-full items-center justify-center">OR</div>
                  <div className="space-y-1 rounded-lg bg-secondary/50 p-3">
                    <p className="text-sm font-medium text-foreground">In your terminal, run:</p>
                    <code className="block font-mono text-sm text-muted-foreground">
                      $ adk login --token {pat?.value || 'loading...'}
                    </code>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
